<template>
  <div>
    <breadcrumb style="margin-left: -15px">
      <breadcrumb-item>
        <a href="/checklists/history?count=10&page=1">Checklist history</a>
      </breadcrumb-item>
      <breadcrumb-item>
        <span>{{ run.checklist_name || 'Checklist run' }}</span>
      </breadcrumb-item>
    </breadcrumb>

    <loading-panel :loading="loading"></loading-panel>

    <card v-if="!loading && run.id" body-classes="p-0">
      <div class="cr-run">
        <div class="cr-run__header">
          <div>
            <h3 class="cr-header__title">{{ run.checklist_name }}</h3>
            <div class="cr-header__meta">
              <span class="cr-pill" :class="statusPillClass(run.status)">{{ statusLabel(run.status) }}</span>
              <span v-if="run.game_name" class="cr-pill">{{ run.game_name }}</span>
              <router-link v-if="run.game_instance_id" :to="'/games/history/instance?id=' + run.game_instance_id" class="checklist-event-link">
                {{ sessionDate(run.booking_date) }}, {{ sessionTime(run.session_start_time) }}
                <i class="tim-icons icon-minimal-right ml-1" style="font-size: 10px"></i>
              </router-link>
              <tag-picker :tags="run.tags || []" :catalog="catalog" :saving="tagsSaving.run" :readonly="!canTag"
                          @add="addTag('run', null, $event)" @remove="removeTag('run', null, $event)"></tag-picker>
            </div>
            <div class="cr-header__people">
              <span>Started by {{ run.r_first_name }} {{ run.r_last_name }} · {{ formatGroupDate(run.start_time) }} {{ formatGroupTime(run.start_time) }}</span>
              <span v-if="run.status && run.status !== 'in_progress'">Completed by {{ run.cb_first_name }} {{ run.cb_last_name }} · {{ formatGroupDate(run.timestamp) }} {{ formatGroupTime(run.timestamp) }}</span>
            </div>
          </div>
          <div class="cr-header__progress">
            <div class="cr-header__progress-label">
              <span class="cr-header__count">{{ completedCount }}<small>/ {{ steps.length }} done</small></span>
              <span v-if="issueCount > 0" class="cr-pill cr-pill--danger">{{ issueCount }} issue{{ issueCount > 1 ? 's' : '' }}</span>
              <span class="cr-header__pct">{{ run.status === 'in_progress' ? progressPercent + '%' : totalTime }}</span>
            </div>
            <div class="cr-progress"><div class="cr-progress__bar" :style="{ width: progressPercent + '%' }"></div></div>
          </div>
          <div class="cr-header__actions">
            <el-tooltip v-if="hasPermissions('checklist_history_delete')" content="Delete run" effect="light" :open-delay="300" placement="top">
              <base-button variant="outlined" tone="danger" class="cr-header__icon-btn" aria-label="Delete run" @click="alert.remove = true">
                <i class="tim-icons icon-trash-simple"></i>
              </base-button>
            </el-tooltip>
            <base-button v-if="canReview && (run.status === 'completed' || run.status === 'awaiting_approval' || run.status === 'approved')" variant="outlined" tone="primary" @click="openReview('reject')">
              <i class="tim-icons icon-simple-remove"></i> {{ run.status === 'awaiting_approval' ? 'Send back' : 'Reject' }}
            </base-button>
            <base-button v-if="canReview && (run.status === 'completed' || run.status === 'awaiting_approval' || run.status === 'rejected')" type="primary" size="sm" class="cr-cta" @click="openReview('approve')">
              <i class="tim-icons icon-check-2 mr-1"></i> Approve
            </base-button>
            <base-button v-if="run.status === 'in_progress'" type="primary" size="sm" class="cr-cta"
                         @click="proxy.$router.push('/checklists/active?run=' + run.id)">
              <i class="tim-icons icon-refresh-02 mr-1"></i> Resume
            </base-button>
          </div>
        </div>

        <div v-if="run.approved_at && run.status !== 'in_progress'" class="cr-run__hints">
          <info-hint :variant="run.status === 'rejected' ? 'danger' : 'success'" :icon="run.status === 'rejected' ? 'icon-simple-remove' : 'icon-check-2'">
            {{ statusLabel(run.status) }} by {{ run.a_first_name }} {{ run.a_last_name }} on {{ formatGroupDate(run.approved_at) }} at {{ formatGroupTime(run.approved_at) }}<template v-if="run.review_note">. {{ run.review_note }}</template>
          </info-hint>
        </div>
        <div v-if="run.approved_at && run.status === 'in_progress'" class="cr-run__hints">
          <info-hint variant="warning" icon="icon-alert-circle-exc">
            Sent back by {{ run.a_first_name }} {{ run.a_last_name }} on {{ formatGroupDate(run.approved_at) }} at {{ formatGroupTime(run.approved_at) }}<template v-if="run.review_note">: {{ run.review_note }}</template>
          </info-hint>
        </div>

        <div v-if="steps.length === 0" class="cr-panel__empty">
          <i class="tim-icons icon-bullet-list-67"></i>
          No steps were recorded on this run
        </div>

        <div v-else class="cr-run__body">
          <div class="cr-run__list">
            <div class="cr-steps">
              <div v-for="step in steps" :key="'S' + step.id"
                   class="cr-step"
                   :class="{ 'cr-step--selected': selectedId === step.id && isWide, 'cr-step--done': step.is_completed === 1 }"
                   @click="selectStep(step)">
                <div class="cr-step__check" :class="{ 'cr-step__check--on': step.is_completed === 1 }" style="cursor: default">
                  <i class="tim-icons icon-check-2"></i>
                </div>
                <div class="cr-step__body">
                  <span class="cr-step__name">{{ step.name }}<span v-if="!step.is_optional" class="cr-step__required" title="Required">*</span></span>
                  <span v-if="step.completed_at" class="cr-step__sub">{{ step.cb_first_name }} {{ step.cb_last_name }} · {{ formatGroupTime(step.completed_at) }} · {{ minutesSinceStart(step.completed_at) }}</span>
                  <span v-else class="cr-step__sub">{{ step.is_skipped === 1 ? 'Skipped' : 'Not done' }}</span>
                </div>
                <div class="cr-step__side">
                  <span v-if="step.note || (step.photo_urls && step.photo_urls.length)" class="cr-step__marks">
                    <i v-if="step.note" class="tim-icons icon-chat-33"></i>
                    <i v-if="step.photo_urls && step.photo_urls.length" class="tim-icons icon-camera-18"></i>
                  </span>
                  <span v-if="step.issue_flagged === 1" class="cr-pill cr-pill--danger">Issue</span>
                  <i class="tim-icons icon-minimal-right d-lg-none"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="cr-run__panel d-none d-lg-block">
            <checklist-step-panel :step="selectedStep" :index="selectedIndex" :total="steps.length" readonly
                                  :catalog="catalog" :can-tag="canTag" :can-create-ticket="canCreateTicket"
                                  :tags-saving="selectedStep ? !!tagsSaving['s' + selectedStep.id] : false"
                                  @add-tag="(st, id) => addTag('step', st, id)"
                                  @remove-tag="(st, tag) => removeTag('step', st, tag)"
                                  @create-ticket="openTicketDrawer" />
          </div>
        </div>
      </div>
    </card>

    <base-drawer :show="drawerOpen && !isWide" :title="'Step ' + (selectedIndex + 1) + ' of ' + steps.length" @close="drawerOpen = false">
      <checklist-step-panel :step="selectedStep" :index="selectedIndex" :total="steps.length" readonly
                            :catalog="catalog" :can-tag="canTag" :can-create-ticket="canCreateTicket"
                            :tags-saving="selectedStep ? !!tagsSaving['s' + selectedStep.id] : false"
                            @add-tag="(st, id) => addTag('step', st, id)"
                            @remove-tag="(st, tag) => removeTag('step', st, tag)"
                            @create-ticket="openTicketDrawer" />
      <template #footer>
        <div class="footer-actions">
          <base-button type="secondary" size="sm" @click="drawerOpen = false">Close</base-button>
        </div>
      </template>
    </base-drawer>

    <!-- Create ticket drawer -->
    <base-drawer :show="ticket.show" title="Create ticket from this step" @close="ticket.show = false">
      <drawer-section title="Project" icon="icon-app">
        <base-input label="Project">
          <el-select class="select-primary" style="width: 100%" placeholder="Choose a project" :value="ticket.projectId" @change="onProjectChange">
            <el-option v-for="p in projects" :key="'PJ' + p.id" class="select-primary" :label="p.name" :value="p.id"></el-option>
          </el-select>
        </base-input>
        <info-hint v-if="projects.length === 0 && !ticket.loadingProjects" variant="warning" icon="icon-alert-circle-exc">
          No projects yet. Create one under Projects first.
        </info-hint>
      </drawer-section>
      <drawer-section title="Ticket" icon="icon-paper">
        <base-input label="Title" v-model="ticket.title" placeholder="Ticket title" :error="ticket.titleError"></base-input>
        <base-input label="Description">
          <textarea class="form-control" rows="8" v-model="ticket.description"></textarea>
        </base-input>
        <div class="row">
          <div class="col-6">
            <base-input label="Status">
              <el-select class="select-primary" style="width: 100%" v-model="ticket.status" :disabled="!ticket.projectId">
                <el-option v-for="st in ticketOptions.statuses" :key="'ST' + st.id" class="select-primary" :label="st.name" :value="st.id"></el-option>
              </el-select>
            </base-input>
          </div>
          <div class="col-6">
            <base-input label="Priority">
              <el-select class="select-primary" style="width: 100%" v-model="ticket.priority">
                <el-option v-for="pr in priorities" :key="'PR' + pr.id" class="select-primary" :label="pr.name" :value="pr.id"></el-option>
              </el-select>
            </base-input>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <base-input label="Type">
              <el-select class="select-primary" style="width: 100%" v-model="ticket.type" :disabled="!ticket.projectId">
                <el-option v-for="tt in ticketOptions.ticket_types" :key="'TT' + tt.id" class="select-primary" :label="tt.name" :value="tt.id"></el-option>
              </el-select>
            </base-input>
          </div>
          <div class="col-6">
            <base-input label="Assignee (optional)">
              <el-select class="select-primary" style="width: 100%" clearable v-model="ticket.assignee" :disabled="!ticket.projectId">
                <el-option v-for="u in ticketOptions.users" :key="'US' + u.id" class="select-primary" :label="u.name" :value="u.id"></el-option>
              </el-select>
            </base-input>
          </div>
        </div>
      </drawer-section>
      <template #footer>
        <div class="footer-actions">
          <base-button type="secondary" size="sm" @click="ticket.show = false">Cancel</base-button>
          <base-button type="primary" size="sm" :disabled="ticket.saving || !ticket.projectId" @click="submitTicket()">Create ticket</base-button>
        </div>
      </template>
    </base-drawer>

    <!-- Review drawer -->
    <base-drawer :show="review.show" :title="reviewTitle" @close="review.show = false">
      <drawer-section title="Review note" icon="icon-chat-33">
        <info-hint v-if="review.decision === 'reject' && run.status === 'awaiting_approval'" variant="warning" icon="icon-alert-circle-exc">
          The run goes back to In progress so the runner can fix it. The note shows on the run page.
        </info-hint>
        <base-input label="Note (optional)">
          <textarea class="form-control" rows="3" v-model="review.note" placeholder="Anything the runner should know"></textarea>
        </base-input>
      </drawer-section>
      <template #footer>
        <div class="footer-actions">
          <base-button type="secondary" size="sm" @click="review.show = false">Cancel</base-button>
          <base-button type="primary" size="sm" :disabled="review.saving" @click="submitReview()">
            {{ reviewAction }}
          </base-button>
        </div>
      </template>
    </base-drawer>

    <base-alert-dialog
      :show="alert.remove"
      title="Delete this run?"
      message="The run and everything recorded on it will be permanently deleted."
      variant="danger"
      show-cancel
      confirm-text="Delete"
      confirm-variant="danger"
      @confirm="deleteRun()"
      @cancel="alert.remove = false"
    />
    <base-alert-dialog
      :show="alert.show"
      :title="alert.title"
      :message="alert.message"
      :variant="alert.variant"
      @confirm="onAlertConfirm()"
      @cancel="alert.show = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { formattedDate, time } from '@/plugins/dateFormatter'

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const group = JSON.parse(localStorage.getItem('group') || '{}')
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${group.id}`

const loading = ref(true)
const run = ref<any>({})
const steps = ref<any[]>([])
const selectedId = ref<number | null>(null)
const drawerOpen = ref(false)
const isWide = ref(true)
const review = reactive({ show: false, decision: 'approve', note: '', saving: false })
const alert = reactive({ show: false, title: '', message: '', variant: 'warning', redirect: '', remove: false })

const completedCount = computed(() => steps.value.filter(s => s.is_completed === 1).length)
const issueCount = computed(() => steps.value.filter(s => s.issue_flagged === 1).length)
const progressPercent = computed(() => steps.value.length ? Math.round((completedCount.value / steps.value.length) * 100) : 0)
const selectedIndex = computed(() => steps.value.findIndex(s => s.id === selectedId.value))
const selectedStep = computed(() => selectedIndex.value >= 0 ? steps.value[selectedIndex.value] : null)
// Sign-off needs its own permission. A runner never reviews their own run unless they are an admin or manager (mirrors the lambda rule).
const profile = JSON.parse(localStorage.getItem('profile') || '{}')
const isAdminOrManager = Number(profile.default_role_id) === 1 || Number(profile.default_role_id) === 2
const canReview = computed(() => {
  if (!hasPermissions('checklist_history_approve')) return false
  const isOwner = Number(run.value?.r_id) === Number(profile.id)
  return !isOwner || isAdminOrManager
})
// Same gate the run page has: anyone who can run checklists can tag. The server enforces it.
const canTag = computed(() => hasPermissions('checklists_submit') || hasPermissions('checklists_view'))
const canCreateTicket = computed(() => hasPermissions('project_ticket_update'))
const catalog = ref<any[]>([])
const tagsSaving = reactive<Record<string, boolean>>({ run: false })
const projects = ref<any[]>([])
const ticketOptions = reactive<{ statuses: any[]; ticket_types: any[]; users: any[] }>({ statuses: [], ticket_types: [], users: [] })
const priorities = [
  { id: 1, name: 'Highest' }, { id: 2, name: 'High' }, { id: 3, name: 'Medium' }, { id: 4, name: 'Low' }, { id: 5, name: 'Lowest' }
]
const ticket = reactive<any>({ show: false, step: null, projectId: null, title: '', description: '', status: null, priority: 3, type: null, assignee: null, titleError: '', saving: false, loadingProjects: false })
const totalTime = computed(() => minutesBetween(run.value.start_time, run.value.timestamp))

const mql = window.matchMedia('(min-width: 992px)')
function onMedia() { isWide.value = mql.matches }

function hasPermissions(scope: string) {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
  return permissions.some((p: string) => p === scope)
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function parseError(err: any): string {
  const data = err?.response?.data
  if (!data) return 'Something went wrong'
  if (typeof data === 'string') {
    try { return JSON.parse(data).error || data } catch (e) { return data }
  }
  return data.error || 'Something went wrong'
}

function showAlert(title: string, message: string, variant = 'warning', redirect = '') {
  alert.title = title
  alert.message = message
  alert.variant = variant
  alert.redirect = redirect
  alert.show = true
}
function onAlertConfirm() {
  alert.show = false
  if (alert.redirect) proxy.$router.push(alert.redirect)
}
function handleError(err: any) {
  const status = err?.response?.status
  if (status === 401 || status === 403) {
    proxy.$router.push('/forbidden')
    return
  }
  if (status === 404) {
    showAlert('Run not found', 'This run no longer exists.', 'warning', '/checklists/history?count=10&page=1')
    return
  }
  showAlert('Error', parseError(err), 'danger')
}

function statusLabel(status: string) {
  const labels: Record<string, string> = { in_progress: 'In progress', completed: 'Completed', awaiting_approval: 'Awaiting approval', approved: 'Approved', rejected: 'Rejected' }
  return labels[status] || 'Completed'
}
function statusPillClass(status: string) {
  const classes: Record<string, string> = { in_progress: 'cr-pill--info', completed: 'cr-pill--success', awaiting_approval: 'cr-pill--warning', approved: '', rejected: 'cr-pill--danger' }
  return classes[status] ?? 'cr-pill--success'
}
function sessionDate(d: string) { return formattedDate(group.date_format, d) }
function sessionTime(t: string) { return time(group.time_format, t) }

function minutesBetween(start: string, end: string) {
  if (!start || !end) return '-'
  const mins = Math.ceil(Math.abs(new Date(end).getTime() - new Date(start).getTime()) / 60000)
  return mins === 1 ? '1 minute' : mins + ' minutes'
}
function minutesSinceStart(at: string) {
  if (!at || !run.value.start_time) return ''
  const mins = Math.max(0, Math.round((new Date(at).getTime() - new Date(run.value.start_time).getTime()) / 60000))
  return '+' + mins + ' min'
}

function selectStep(step: any) {
  selectedId.value = step.id
  if (!isWide.value) drawerOpen.value = true
}

async function loadRun() {
  loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/checklists/instances/${proxy.$route.query.id}`, config)
    run.value = response.data.run
    steps.value = response.data.steps
    if (selectedId.value === null && steps.value.length) selectedId.value = steps.value[0].id
  } catch (err) {
    handleError(err)
  } finally {
    loading.value = false
  }
}

async function loadCatalog() {
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/tags`, config)
    catalog.value = Array.isArray(response.data) ? response.data : []
  } catch (e) {
    catalog.value = []
  }
}

async function setTags(target: 'run' | 'step', step: any, ids: number[]) {
  const key = target === 'run' ? 'run' : 's' + step.id
  if (tagsSaving[key]) return
  tagsSaving[key] = true
  const holder = target === 'run' ? run.value : step
  const previous = holder.tags || []
  try {
    const config = await authConfig()
    const response = await axios.put(`${baseUrl}/checklists/instances`,
      { id: run.value.id, action: 'set_tags', target, step_id: target === 'step' ? step.id : undefined, tag_ids: ids }, config)
    holder.tags = response.data.tags || []
  } catch (err: any) {
    holder.tags = previous
    proxy.$notify({ type: 'danger', message: 'Failed to update tags', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    tagsSaving[key] = false
  }
}
function addTag(target: 'run' | 'step', step: any, tagId: number) {
  const holder = target === 'run' ? run.value : step
  const current = (holder.tags || []).map((t: any) => t.id)
  if (current.includes(tagId)) return
  setTags(target, step, [...current, tagId])
}
function removeTag(target: 'run' | 'step', step: any, tag: any) {
  const holder = target === 'run' ? run.value : step
  setTags(target, step, (holder.tags || []).map((t: any) => t.id).filter((id: number) => id !== tag.id))
}

// ── Create ticket from a flagged step ──────────────────────────────────────
function escapeHtml(text: string) {
  return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
async function openTicketDrawer(step: any) {
  ticket.step = step
  ticket.projectId = null
  ticket.status = null
  ticket.type = null
  ticket.assignee = null
  ticket.priority = 3
  ticket.titleError = ''
  ticket.title = `${run.value.checklist_name}: ${step.name}`
  const lines = [
    `Checklist: ${run.value.checklist_name}`,
    `Step: ${step.name}`
  ]
  if (step.issue_flagged === 1) lines.push(`Issue: ${step.issue_description || 'Flagged without a description'}`)
  if (step.note) lines.push(`Note: ${step.note}`)
  if (step.photo_urls && step.photo_urls.length) lines.push('Photos:', ...step.photo_urls)
  lines.push(`Run: ${window.location.origin}/checklists/history/instance?id=${run.value.id}`)
  ticket.description = lines.join('\n')
  ticket.show = true
  if (projects.value.length === 0) {
    ticket.loadingProjects = true
    try {
      const config = await authConfig()
      const response = await axios.get(`${baseUrl}/projects?count=200&offset=0`, config)
      projects.value = response.data.projects || []
    } catch (e) {
      projects.value = []
    } finally {
      ticket.loadingProjects = false
    }
  }
}
async function onProjectChange(projectId: number) {
  ticket.projectId = projectId
  ticket.status = null
  ticket.type = null
  ticket.assignee = null
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/projects/${projectId}?tab=0&count=1&offset=0`, config)
    ticketOptions.statuses = response.data.statuses || []
    ticketOptions.ticket_types = response.data.ticket_types || []
    ticketOptions.users = response.data.users || []
    ticket.status = ticketOptions.statuses.length ? ticketOptions.statuses[0].id : null
    ticket.type = ticketOptions.ticket_types.length ? ticketOptions.ticket_types[0].id : null
  } catch (err) {
    handleError(err)
  }
}
async function submitTicket() {
  ticket.titleError = ''
  if (!ticket.title.trim()) {
    ticket.titleError = 'Give the ticket a title'
    return
  }
  ticket.saving = true
  try {
    const config = await authConfig()
    const linkify = (line: string) => line.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>')
    const description = escapeHtml(ticket.description).split('\n').map((l: string) => `<p>${linkify(l) || '&nbsp;'}</p>`).join('')
    const created = await axios.post(`${baseUrl}/projects/${ticket.projectId}/tickets`, {
      title: ticket.title.trim(),
      description,
      assignee: ticket.assignee || null,
      status: ticket.status,
      priority: ticket.priority,
      ticket_type: ticket.type,
      time_estimate_minutes: null,
      due_date: null
    }, config)
    const ticketId = created.data && created.data.id
    const project = projects.value.find((pj: any) => pj.id === ticket.projectId)
    const identifier = (project && project.identifier ? project.identifier + '-' : '') + (created.data && created.data.identifier)
    let linked = false
    for (let attempt = 0; attempt < 2 && !linked; attempt += 1) {
      try {
        await axios.put(`${baseUrl}/checklists/instances`, { id: run.value.id, action: 'link_ticket', step_id: ticket.step.id, work_ticket_id: ticketId }, config)
        linked = true
      } catch (e) { /* retry once */ }
    }
    ticket.show = false
    if (linked) {
      proxy.$notify({ type: 'success', message: `Ticket ${identifier} created`, icon: 'tim-icons icon-check-2' })
      await loadRun()
    } else {
      showAlert('Ticket created but not linked', `Ticket ${identifier} was created but could not be linked to the step. Open it from the project board.`, 'warning')
    }
  } catch (err) {
    handleError(err)
  } finally {
    ticket.saving = false
  }
}

const reviewAction = computed(() => {
  if (review.decision === 'approve') return 'Approve'
  return run.value && run.value.status === 'awaiting_approval' ? 'Send back' : 'Reject'
})
const reviewTitle = computed(() => (review.decision === 'approve' ? 'Approve run' : (reviewAction.value === 'Send back' ? 'Send back' : 'Reject run')))

function openReview(decision: string) {
  review.decision = decision
  review.note = ''
  review.show = true
}

async function submitReview() {
  review.saving = true
  try {
    const config = await authConfig()
    const res = await axios.put(`${baseUrl}/checklists/instances`,
      { id: run.value.id, action: 'review', decision: review.decision, review_note: review.note }, config)
    review.show = false
    const newStatus = res.data && res.data.status
    const message = newStatus === 'in_progress' ? 'Run sent back' : (review.decision === 'approve' ? 'Run approved' : 'Run rejected')
    proxy.$notify({ type: 'success', message, icon: 'tim-icons icon-check-2' })
    await loadRun()
  } catch (err) {
    handleError(err)
  } finally {
    review.saving = false
  }
}

async function deleteRun() {
  alert.remove = false
  loading.value = true
  try {
    const config = await authConfig()
    await axios.delete(`${baseUrl}/checklists/instances/${run.value.id}`, config)
    proxy.$notify({ type: 'success', message: 'Run deleted', icon: 'tim-icons icon-check-2' })
    proxy.$router.push('/checklists/history?count=10&page=1')
  } catch (err) {
    loading.value = false
    handleError(err)
  }
}

onMounted(() => {
  onMedia()
  mql.addEventListener('change', onMedia)
  loadCatalog()
  loadRun()
})
onBeforeUnmount(() => { mql.removeEventListener('change', onMedia) })
</script>

<script lang="ts">
import { Select, Option } from 'element-ui'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue'
import BreadcrumbItem from '@/components/Breadcrumb/BreadcrumbItem.vue'
import ChecklistStepPanel from './ChecklistStepPanel.vue'

export default {
  name: 'checklist-instance',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    Breadcrumb,
    BreadcrumbItem,
    ChecklistStepPanel
  }
}
</script>

<style scoped>
.checklist-event-link { color: #e14eca; text-decoration: none; }
.checklist-event-link:hover { color: #e14eca; text-decoration: underline; }
</style>
